import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, DatePicker, Descriptions, Space, Table, Tabs } from 'antd';
import dayjs from 'dayjs';
import client from '../../api/client';

const pages = { HOME: '首页', CATEGORY: '分类', PRODUCT: '商品详情', SEARCH: '搜索', PRICE_MONITOR: '价格监控', PROFILE: '我的', MEMBERSHIP: '会员', WALLET: '钱包', REBATE: '返利', INVITE: '邀请', MESSAGES: '消息', SETTINGS: '设置', PRIVACY: '隐私管理', HELP: '帮助', DISCOVERY: '发现', DEALS: '优惠专区', AI_GUIDE: 'AI 导购', LOGIN: '登录', OTHER: '其他' };
const events = { PAGE_VIEW: '页面浏览', SEARCH: '主动搜索', BUY_LINK: '请求购买链接', PRICE_ALERT: '设置价格提醒', AI_GUIDE: '发起 AI 导购' };
const countColumns = [{ title: '已授权登录账号', dataIndex: 'loggedUsers' }, { title: '已授权匿名安装', dataIndex: 'anonymousInstalls' }];
const retention = (value, row) => value == null ? '尚未到观察日结束' : `${value} / ${row.newSubjects}（${row.newSubjects ? (value * 100 / row.newSubjects).toFixed(1) : 0}%）`;

export default function Analytics() {
  const [range, setRange] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const from = range?.[0]?.format('YYYY-MM-DD');
  const to = range?.[1]?.format('YYYY-MM-DD');
  useEffect(() => {
    let current = true;
    setLoading(true); setError(false); setReport(null);
    client.get('/analytics', { params: { from, to } }).then((data) => { if (current) setReport(data); })
      .catch(() => { if (current) setError(true); }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [from, to, refresh]);
  const table = (rows, columns, key) => <Table rowKey={key} dataSource={rows || []} columns={columns} loading={loading} size="small" pagination={{ pageSize: 15 }} />;
  return <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
    <h2>使用统计</h2>
    <Alert type="info" showIcon title="仅统计主动授权的用户样本"
      description="默认关闭统计。拒绝或撤回的用户不计入本报表，因此不能代表全量日活。登录账号按账号去重；匿名按授权期间的随机安装标识去重，两者不可相加为人数。数据保留30天，时区为 Asia/Shanghai，今天的数据尚未完整。" />
    <Space wrap>
      <DatePicker.RangePicker value={range} onChange={setRange} placeholder={['开始日期（默认近7天）', '结束日期']}
        disabledDate={(date) => date.isBefore(dayjs().subtract(29, 'day'), 'day') || date.isAfter(dayjs(), 'day')} />
      <Button onClick={() => setRefresh((n) => n + 1)} loading={loading}>刷新</Button>
    </Space>
    {error && <Alert type="error" title="统计加载失败，请刷新重试" />}
    <Card title="截至所选结束日的活跃" loading={loading}>
      <Descriptions column={3}>
        {[['days1', '日活 DAU'], ['days7', '周活 WAU'], ['days30', '月活 MAU']].map(([key, label]) =>
          <Descriptions.Item key={key} label={label}>{report?.active?.[key]
            ? `${report.active[key].loggedUsers} 个账号 / ${report.active[key].anonymousInstalls} 个匿名安装` : '完整窗口数据不可用'}</Descriptions.Item>)}
      </Descriptions>
      <span style={{ color: '#666' }}>活跃以 APP 前台活动计算；账号切换或撤回后需重新授权，重新授权的匿名安装无法跨授权周期去重。</span>
    </Card>
    <Tabs items={[
      { key: 'daily', label: '每日活跃', children: table(report?.daily, [{ title: '日期', dataIndex: 'day' }, ...countColumns], 'day') },
      { key: 'retention', label: '新观测与留存', children: <>
        <p>新观测指该统计主体首次出现前台活动，不等于新增注册。次日/7日留存按第1/7个自然日是否活跃计算；观察日结束后才展示。删除数据可能使历史结果下降。</p>
        {table(report?.retention, [{ title: '首次观测日期', dataIndex: 'day' }, { title: '主体类型', dataIndex: 'kind', render: (v) => v === 'LOGGED' ? '登录账号' : '匿名安装' }, { title: '新观测数', dataIndex: 'newSubjects' }, { title: '次日留存', dataIndex: 'retained1', render: retention }, { title: '7日留存', dataIndex: 'retained7', render: retention }], (r) => `${r.day}-${r.kind}`)}
      </> },
      { key: 'features', label: '功能使用', children: table(report?.features, [{ title: '页面', dataIndex: 'pageName', render: (v) => pages[v] || v }, { title: '事件', dataIndex: 'eventName', render: (v) => events[v] || v }, { title: '次数', dataIndex: 'eventCount' }, ...countColumns], (r) => `${r.pageName}-${r.eventName}`) },
      { key: 'funnel', label: '商品漏斗', children: <><p>所选日期内，同一授权安装依次发生主动搜索 → 商品详情浏览 → 请求购买链接。购买链接请求不代表成交，跨安装行为不拼接。</p>{table(report?.funnel, [{ title: '主体类型', dataIndex: 'kind', render: (v) => v === 'LOGGED' ? '登录账号' : '匿名安装' }, { title: '搜索', dataIndex: 'searched' }, { title: '商品详情', dataIndex: 'viewed' }, { title: '购买链接', dataIndex: 'buyLink' }], 'kind')}</> },
      { key: 'versions', label: '版本分布', children: table(report?.versions, [{ title: '版本', dataIndex: 'label' }, ...countColumns], 'label') },
      { key: 'channels', label: '渠道分布', children: table(report?.channels, [{ title: '渠道', dataIndex: 'label' }, ...countColumns], 'label') },
    ]} />
  </Space>;
}
